import type {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError, AxiosRequestConfig } from "axios";

import type { IQuery } from "@citrine/client";
import ClientBase from "@citrine/client";
import { getUniqueArray } from "@citrine/utils/helpers";
import { roundValue } from "@citrine/utils/rounding";

import type { IFormulationConstraint } from "../constraint";
import { isFormulationFractionConstraint } from "../constraint";
import { DesignSpaceSummaryClient } from "../design-space-summary";

import type {
  DefaultDesignSpaceMode,
  DesignSpaceEntity,
  Dimension,
  HierarchicalDesignSpaceConfig,
  HierarchicalNodeConnections,
  IDesignSpaceConfig,
  IFormulationDesignSpaceConfig,
  IProductDesignSpaceConfig,
  LockedReasons,
  MaterialNodeDefinition,
} from "./design-space";
import {
  isContinuousDimension,
  isEnumeratedDimension,
  isFormulationDesignSpaceConfig,
  isProductDesignSpaceConfig,
  transformDesignSpaceConfig,
} from "./design-space";

export type RenameDesignSpace = {
  id: string;
} & (
  | {
      name: string;
      description?: string;
    }
  | {
      name?: undefined;
      description: string;
    }
);

export interface ValidateVariables {
  config: IDesignSpaceConfig;
  predictorId?: string;
}

export interface DesignSpaceQuery extends IQuery {
  page?: number;
  per_page?: number;
  name?: string;
  archived?: boolean;
  // currently limited to : create_time, update_time and name.
  // create_time.asc
  // update_time.desc
  sort_by?: string;
}

// format the default design space config to round the fraction constraints
function formatDefaultDesignSpaceConfig<
  Config extends IProductDesignSpaceConfig | HierarchicalDesignSpaceConfig,
>(config: Config): Config {
  return isProductDesignSpaceConfig(config)
    ? {
        ...config,
        dimensions: formatDimensions(config?.dimensions),
        subspaces: config.subspaces?.map((subspace) =>
          isFormulationDesignSpaceConfig(subspace)
            ? formatFormulationConfig(subspace)
            : subspace,
        ),
      }
    : {
        ...config,
        root: formatMaterialNodeDefinition(config.root),
        subspaces: config.subspaces.map(formatMaterialNodeDefinition),
      };
}

function formatMaterialNodeDefinition(
  material: MaterialNodeDefinition,
): MaterialNodeDefinition {
  return {
    ...material,
    attributes: formatDimensions(material.attributes),
    formulation: material.formulation
      ? formatFormulationConfig(material.formulation)
      : undefined,
  };
}

function formatFormulationConfig(
  config: IFormulationDesignSpaceConfig,
): IFormulationDesignSpaceConfig {
  return {
    ...config,
    constraints: config.constraints?.map(
      (constraint: IFormulationConstraint) =>
        isFormulationFractionConstraint(constraint)
          ? {
              ...constraint,
              min: Number(roundValue(constraint.min, -1)),
              max: Number(roundValue(constraint.max, 1)),
            }
          : constraint,
    ),
  };
}

function formatDimensions(dimensions?: Dimension[]) {
  return dimensions?.map<Dimension>((dimension) =>
    isEnumeratedDimension(dimension)
      ? {
          ...dimension,
          // fix for a bug where request returns duplicates
          list: getUniqueArray(dimension.list),
        }
      : isContinuousDimension(dimension)
        ? {
            ...dimension,
            lower_bound: Number(roundValue(dimension.lower_bound, -1)),
            upper_bound: Number(roundValue(dimension.upper_bound, 1)),
          }
        : dimension,
  );
}

export class DesignSpaceClient extends ClientBase<
  DesignSpaceEntity,
  string,
  DesignSpaceQuery
> {
  protected constructor(protected readonly projectId: string) {
    super({
      endpoint: `/api/v3/projects/${projectId}/design-spaces`,
      findField: "response",
      defaultQuery: {
        page: 1,
        per_page: 20,
        name: undefined,
        archived: undefined,
        sort_by: undefined,
      },
    });
  }

  static getInstance(projectId: string) {
    return new this(projectId);
  }

  createAndValidate(
    designSpacePayload: Partial<DesignSpaceEntity>,
  ): Promise<DesignSpaceEntity> {
    return this.create(designSpacePayload).then((designSpace) =>
      this.longValidate(designSpace.id),
    );
  }

  useCreateAndValidate(
    options: Omit<
      UseMutationOptions<
        DesignSpaceEntity,
        AxiosError,
        Partial<DesignSpaceEntity>
      >,
      "mutationFn"
    > = {},
  ) {
    return useMutation({
      ...options,
      mutationFn: (data) => this.createAndValidate(data),
    });
  }

  updateAndValidate(
    designSpacePayload: DesignSpaceEntity,
  ): Promise<DesignSpaceEntity> {
    return super
      .update(designSpacePayload)
      .then((designSpace) => this.longValidate(designSpace.id));
  }

  useUpdateAndValidate(
    options: Omit<
      UseMutationOptions<DesignSpaceEntity, AxiosError, DesignSpaceEntity>,
      "mutationFn"
    > = {},
  ) {
    return useMutation({
      ...options,
      mutationFn: (data) => this.updateAndValidate(data),
    });
  }

  async lockedReasons(id: string) {
    const result = await this.request<LockedReasons>({
      method: "GET",
      url: `${this.endpoint}/${id}/locked-reasons`,
    });
    return result;
  }

  useLockedReasons(
    id: string,
    options: Omit<UseQueryOptions<LockedReasons>, "queryKey" | "queryFn"> = {},
  ) {
    return useQuery({
      ...options,
      queryKey: [...this.getModelCacheKey(id), "locked-reasons"],
      queryFn: () => this.lockedReasons(id),
      enabled: Boolean(id && (options.enabled ?? true)),
    });
  }

  async archive(id: string) {
    const result = await this.request<DesignSpaceEntity>({
      method: "PUT",
      url: `${this.endpoint}/${id}/archive`,
    });

    this.invalidateAfterMutation(result.id);
    this.addModelToCache(result);

    return result;
  }

  useArchive(
    options: Omit<
      UseMutationOptions<DesignSpaceEntity, AxiosError, string>,
      "mutationFn"
    > = {},
  ) {
    return useMutation({ ...options, mutationFn: (id) => this.archive(id) });
  }

  async rename({ id, description, name }: RenameDesignSpace) {
    const result = await this.request<DesignSpaceEntity>({
      method: "PUT",
      url: `${this.endpoint}/${id}/rename`,
      data: { description, name },
    });
    this.invalidateAfterMutation(result.id);
    return result;
  }

  useRename(
    options: Omit<
      UseMutationOptions<DesignSpaceEntity, AxiosError, RenameDesignSpace>,
      "mutationFn"
    > = {},
  ) {
    return useMutation({ ...options, mutationFn: (data) => this.rename(data) });
  }

  async restore(id: string) {
    const result = await this.request<DesignSpaceEntity>({
      method: "PUT",
      url: `${this.endpoint}/${id}/restore`,
    });

    this.invalidateAfterMutation(result.id);
    this.addModelToCache(result);

    return result;
  }

  useRestore(
    options: Omit<
      UseMutationOptions<DesignSpaceEntity, AxiosError, string>,
      "mutationFn"
    > = {},
  ) {
    return useMutation({ ...options, mutationFn: (id) => this.restore(id) });
  }

  async validate(
    config: IDesignSpaceConfig,
    predictorId?: string,
  ): Promise<{ errors?: string[] }> {
    const { result } = await this.request<{ result?: string[] }>({
      method: "POST",
      url: `${this.endpoint}/fast-validate`,
      params: {
        predictor: predictorId,
      },
      data: {
        instance: config,
      },
    });

    // the result is an array of error messages
    return { errors: result?.length ? result : undefined };
  }

  useValidate(
    options: Omit<
      UseMutationOptions<{ errors?: string[] }, AxiosError, ValidateVariables>,
      "mutationFn"
    > = {},
  ): UseMutationResult<{ errors?: string[] }, AxiosError, ValidateVariables> {
    return useMutation<{ errors?: string[] }, AxiosError, ValidateVariables>({
      ...options,
      mutationFn: ({ config, predictorId }: ValidateVariables) =>
        this.validate(config, predictorId),
    });
  }

  async longValidate(id: string) {
    const result = await this.request<DesignSpaceEntity>({
      method: "PUT",
      url: `${this.endpoint}/${id}/validate`,
    });
    this.invalidateAfterMutation(id);
    this.addModelToCache(result);
    return result;
  }

  useLongValidate(
    options: Omit<
      UseMutationOptions<DesignSpaceEntity, AxiosError, string>,
      "mutationFn"
    > = {},
  ) {
    return useMutation({
      mutationFn: (id) => this.longValidate(id),
      ...options,
    });
  }

  async getDefaultDesignSpaceConfig<
    Mode extends DefaultDesignSpaceMode,
    DSConfig extends (Mode extends "HIERARCHICAL"
      ? HierarchicalDesignSpaceConfig
      : IProductDesignSpaceConfig),
  >({
    predictorId,
    predictorVersion,
    mode,
    constraintsFromTrainingData,
  }: {
    predictorId: string;
    predictorVersion: string;
    mode: Mode;
    constraintsFromTrainingData: boolean;
  }): Promise<DSConfig> {
    const { instance } = await this.request<{
      instance: DSConfig;
    }>({
      method: "POST",
      url: `${this.endpoint}/default`,
      data: {
        predictor_id: predictorId,
        predictor_version: predictorVersion,
        mode,
        ...(constraintsFromTrainingData
          ? {
              include_ingredient_fraction_constraints: false,
              include_label_fraction_constraints: true,
              include_label_count_constraints: true,
              include_parameter_constraints: true,
            }
          : {}),
      },
    });

    return formatDefaultDesignSpaceConfig(instance);
  }

  useGetDefaultDesignSpaceConfig<
    Mode extends DefaultDesignSpaceMode,
    DSConfig extends (Mode extends "HIERARCHICAL"
      ? HierarchicalDesignSpaceConfig
      : IProductDesignSpaceConfig),
  >(
    {
      predictorId,
      predictorVersion,
      mode,
      constraintsFromTrainingData,
    }: {
      predictorId: string | undefined;
      predictorVersion: string | undefined;
      mode: Mode;
      constraintsFromTrainingData: boolean;
    },
    options: Omit<UseQueryOptions<DSConfig>, "queryKey" | "queryFn"> = {},
  ) {
    return useQuery<DSConfig>({
      ...options,
      queryKey: [
        `${this.endpoint}/default`,
        `default-for-${predictorId}-${predictorVersion}`,
        mode,
        constraintsFromTrainingData,
      ],
      queryFn: () =>
        this.getDefaultDesignSpaceConfig({
          predictorId: predictorId!,
          predictorVersion: predictorVersion!,
          mode,
          constraintsFromTrainingData,
        }),
      enabled: Boolean(
        predictorId && predictorVersion && (options.enabled ?? true),
      ),
    });
  }

  async getHierarchicalNodeConnections(config?: HierarchicalDesignSpaceConfig) {
    const response = await this.request<{
      connections: HierarchicalNodeConnections;
    }>({
      method: "POST",
      url: `${this.endpoint}/node-connections`,
      data: {
        nodes: config ? [config.root, ...config.subspaces] : [],
      },
    });
    return response.connections;
  }

  useGetHierarchicalNodeConnections(
    config?: HierarchicalDesignSpaceConfig,
    options?: Omit<
      UseQueryOptions<HierarchicalNodeConnections>,
      "queryKey" | "queryFn"
    >,
  ) {
    return useQuery({
      ...options,
      enabled: config && (options?.enabled ?? true),
      queryKey: [
        `${this.endpoint}/node-connections`,
        config?.root,
        ...(config?.subspaces ?? []),
      ],
      queryFn: () => this.getHierarchicalNodeConnections(config),
    });
  }

  invalidateAfterMutation(id?: string) {
    super.invalidateAfterMutation(id);
    DesignSpaceSummaryClient.getInstance(
      this.projectId,
    ).invalidateAfterMutation(id);
  }

  protected createConfig({
    metadata,
    ...model
  }: Partial<DesignSpaceEntity>): AxiosRequestConfig {
    return {
      ...super.createConfig(model),
      data: {
        ...super.createConfig(model).data,
        settings: metadata?.settings,
      },
    };
  }

  protected fromModel(model: Partial<DesignSpaceEntity>) {
    // create and update only take the config, not the entire entity object
    return {
      instance: model.data?.instance,
    };
  }

  protected toModel(payload: any): DesignSpaceEntity {
    const model = super.toModel(payload);
    transformDesignSpaceConfig(model.data.instance);
    return model;
  }
}
